/**
 * staleTime strategy:
 *   30s — alert list and pending count (real-time awareness of new alerts)
 *   5min — transaction logs (historical, rarely change once written)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PendingAlertsCount, TransactionLogEntry } from '../services/alerts';
import {
  acknowledgeAlert,
  dismissAlert,
  fetchAlerts,
  fetchPendingAlertsCount,
  fetchTransactionLogs,
  resolveAlert,
} from '../services/alerts';
import type { AlertFilters, AlertResolutionType, TransactionAlert } from '../types/alerts';
import { preserveSameBookAlerts } from './bookPlaceholders';

export const alertsKeys = {
  all: ['transaction-alerts'] as const,
  lists: () => [...alertsKeys.all, 'list'] as const,
  pendingCount: (clientId?: string | null) => [...alertsKeys.all, 'pending-count', clientId ?? null] as const,
  logs: (transactionId: string) => [...alertsKeys.all, 'logs', transactionId] as const,
};

export function useAlerts(filters: AlertFilters) {
  return useQuery<{ alerts: TransactionAlert[]; count: number; hasMore: boolean; nextCursor: string | null }, Error>({
    queryKey: [...alertsKeys.lists(), filters],
    queryFn: () => fetchAlerts(filters),
    staleTime: 1000 * 30,
    refetchInterval: () => 30_000 + Math.floor(Math.random() * 9_000),
    refetchOnWindowFocus: true,
    placeholderData: (previous, previousQuery) =>
      preserveSameBookAlerts(previous, filters, previousQuery?.queryKey[2] as AlertFilters | undefined),
  });
}

export function usePendingAlertsCount(enabled = true, clientId?: string | null) {
  return useQuery<PendingAlertsCount, Error>({
    queryKey: alertsKeys.pendingCount(clientId),
    queryFn: () => fetchPendingAlertsCount(clientId),
    staleTime: 1000 * 30,
    enabled,
  });
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (alertId: string) => acknowledgeAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertsKeys.all });
    },
  });
}

export function useResolveAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ alertId, resolutionType, notes }: { alertId: string; resolutionType: AlertResolutionType; notes?: string }) =>
      resolveAlert(alertId, resolutionType, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertsKeys.all });
    },
  });
}

export function useDismissAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (alertId: string) => dismissAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertsKeys.all });
    },
  });
}

export function useTransactionLogs(transactionId: string | null, enabled: boolean) {
  return useQuery<TransactionLogEntry[], Error>({
    queryKey: alertsKeys.logs(transactionId ?? ''),
    queryFn: () => fetchTransactionLogs(transactionId ?? ''),
    enabled: enabled && !!transactionId,
    staleTime: 1000 * 60 * 5,
  });
}
